import { useQueryClient } from '@tanstack/react-query';

import { useApiClient, useToastService } from '@@common';

import { ClientModel, EnvironmentClient, EnvironmentModel, ReleasePlanCreatedResult } from '../../../api/api';

type ClientDeploymentOperation = 'addClient' | 'redeployClient';

interface UseEnvironmentClientActionsOptions {
	onReleasePlanCreated?: (releasePlanId: string, environment: EnvironmentModel) => void;
	onAfterAction?: () => void | Promise<void>;
}

export default function useEnvironmentClientActions({ onReleasePlanCreated, onAfterAction }: UseEnvironmentClientActionsOptions = {}) {
	const environmentClient = useApiClient(EnvironmentClient) as EnvironmentClient;
	const toastService = useToastService();
	const queryClient = useQueryClient();

	const refresh = async () => {
		await queryClient.invalidateQueries({ queryKey: ['environments'] });
		await queryClient.invalidateQueries({ queryKey: ['clients'] });
		await onAfterAction?.();
	};

	const handleReleasePlan = (result: ReleasePlanCreatedResult, environment: EnvironmentModel) => {
		if (result.releasePlanId) {
			onReleasePlanCreated?.(result.releasePlanId, environment);
		}
	};

	const deployClient = async (environment: EnvironmentModel, client: ClientModel, operation: ClientDeploymentOperation = 'addClient') => {
		try {
			const result = await environmentClient.addEnvironmentClient(environment.id, { clientId: client.id });
			toastService.success(`Client "${client.name}" ${operation === 'redeployClient' ? 'redeploy' : 'deployment'} queued for ${environment.name}.`);
			await refresh();
			handleReleasePlan(result, environment);
			return result;
		} catch (error) {
			console.error('Error deploying client:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to deploy client: ${errorMessage}`);
			throw error;
		}
	};

	const removeClient = async (environment: EnvironmentModel, client: ClientModel) => {
		try {
			const result = await environmentClient.removeEnvironmentClient(environment.id, client.id);
			toastService.success(`Client "${client.name}" removal queued for ${environment.name}.`);
			await refresh();
			handleReleasePlan(result, environment);
			return result;
		} catch (error) {
			console.error('Error removing client from environment:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to remove client: ${errorMessage}`);
			throw error;
		}
	};

	const rotateClientSecret = async (environment: EnvironmentModel, client: ClientModel) => {
		try {
			const result = await environmentClient.rotateEnvironmentClientSecret(environment.id, client.id);
			toastService.success(`Client "${client.name}" secret rotation queued for ${environment.name}.`);
			await refresh();
			handleReleasePlan(result, environment);
			return result;
		} catch (error) {
			console.error('Error rotating environment client secret:', error);
			const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
			toastService.error(`Failed to rotate secret: ${errorMessage}`);
			throw error;
		}
	};

	return {
		deployClient,
		removeClient,
		rotateClientSecret,
		refreshEnvironmentClients: refresh,
	};
}
